/**
 * Place of supply → INTRA or INTER, and the CGST/SGST/IGST split.
 *
 * This is the single point where "which tax applies" is decided. The editor,
 * the preview, the PDF, print and every report consume the result of
 * `splitGst` via `computeDocument` — nobody re-derives it.
 */
import { isValidStateCode } from './stateCodes';
import type { Money } from '../money/types';
import { asMoney } from '../money/types';
import { roundPaise } from '../money/paise';

export type SupplyType = 'INTRA' | 'INTER' | 'EXPORT' | 'SEZ';

export interface TaxSplit {
  readonly cgstRateBp: number;
  readonly sgstRateBp: number;
  readonly igstRateBp: number;
  readonly cgstPaise: Money;
  readonly sgstPaise: Money;
  readonly igstPaise: Money;
}

const ZERO_SPLIT: TaxSplit = Object.freeze({
  cgstRateBp: 0,
  sgstRateBp: 0,
  igstRateBp: 0,
  cgstPaise: asMoney(0),
  sgstPaise: asMoney(0),
  igstPaise: asMoney(0),
});

/**
 * Where the "place of supply" came from.
 *
 * Recorded on the document so an audit can answer "why was this invoice
 * intra-state?" without re-deriving it from a customer record that has since
 * been edited.
 *
 * `UNRESOLVED` is a first-class outcome, not an error. It is what tells the UI to
 * stop and ask the user rather than guess.
 */
export type PlaceOfSupplySource = 'OVERRIDE' | 'DELIVERY' | 'CUSTOMER' | 'UNRESOLVED';

/** Why a place of supply could not be established. Drives the message the user sees. */
export type PlaceOfSupplyUnresolvedReason =
  /** The customer record has no state, and no delivery or override was given. */
  | 'NO_CUSTOMER_STATE'
  /** An explicit override was supplied but is not a real GST state code. */
  | 'INVALID_OVERRIDE'
  /** A delivery state was supplied but is not a real GST state code. */
  | 'INVALID_DELIVERY'
  /** No source at all was supplied (no customer, no delivery, no override). */
  | 'NO_SOURCE';

export interface PlaceOfSupplyInput {
  /**
   * Where the supplier is registered.
   *
   * Used ONLY to classify the resolved code as INTRA or INTER. It is NEVER a
   * source of the place of supply, and it is never a fallback — see the decision
   * below.
   */
  businessStateCode: string;
  /** The customer's registered state, from the snapshot. */
  customerStateCode: string;
  /**
   * State the goods are actually delivered to, when that differs from the
   * customer's registered address.
   *
   * For goods the place of supply follows the place of DELIVERY, not the
   * customer's billing address — a customer registered in Hyderabad who takes
   * delivery in Karnataka is a Karnataka supply. 1.6.x had no notion of this.
   */
  deliveryStateCode?: string | null;
  /**
   * An explicit, user-chosen place of supply. Wins over everything, but is
   * VALIDATED: an override naming a state that does not exist is reported as
   * invalid rather than being passed through to produce a tax split against a
   * state code that means nothing.
   */
  overrideStateCode?: string | null;
}

export type PlaceOfSupplyResolution =
  | {
      readonly resolved: true;
      readonly code: string;
      readonly supplyType: SupplyType;
      readonly source: Exclude<PlaceOfSupplySource, 'UNRESOLVED'>;
    }
  | {
      readonly resolved: false;
      readonly code: null;
      readonly supplyType: null;
      readonly source: 'UNRESOLVED';
      readonly reason: PlaceOfSupplyUnresolvedReason;
    };


/**
 * Resolve the place of supply, and say which input won.
 *
 * THE LOCKED DECISION
 * -------------------
 *   explicit valid override
 *     -> delivery / destination information
 *     -> appropriate customer / transaction information
 *     -> otherwise REQUIRE THE USER TO PROVIDE IT
 *
 * Two things this function deliberately does NOT do, because both were wrong
 * before and both produced a real tax document with the wrong tax on it:
 *
 * 1. **It never falls back to the business state.** The supplier's state says
 *    where the business is; it is not where the supply happens. Falling back to
 *    it charges CGST + SGST on interstate sales and IGST on intrastate ones,
 *    silently, and the document still reconciles internally so no report and no
 *    totals-only test would ever catch it. When nothing resolves, the answer is
 *    `UNRESOLVED` and the caller is expected to BLOCK THE ISSUE and ask.
 *
 * 2. **It never treats reverse charge as a place-of-supply source.** Reverse
 *    charge changes who is the recipient of supply, which is a real and separate
 *    question. Conflating the two means a reverse-charge sale is tax-split by a
 *    rule the user never chose. It is simply not an input here.
 *
 * WHY THE RESULT IS A UNION, NOT A STRING
 * ---------------------------------------
 * Returning `code: string` forced every caller to invent its own fallback for
 * the empty case, and each invented a different one. Making "not determinable"
 * representable in the type means the write path cannot forget to handle it.
 */
export function resolvePlaceOfSupply(input: PlaceOfSupplyInput): PlaceOfSupplyResolution {
  const override = (input.overrideStateCode ?? '').trim();
  const delivery = (input.deliveryStateCode ?? '').trim();
  const customer = (input.customerStateCode ?? '').trim();

  // Each candidate is validated as it is considered, and a BAD one is reported
  // rather than skipped. Silently ignoring an invalid delivery state and falling
  // through to the customer state would produce a confidently wrong tax split
  // from a typo — the user asked for a specific state and got a different one.
  if (override) {
    if (!isValidStateCode(override)) {
      return { resolved: false, code: null, supplyType: null, source: 'UNRESOLVED', reason: 'INVALID_OVERRIDE' };
    }
    return finish(override, 'OVERRIDE', input.businessStateCode);
  }

  if (delivery) {
    if (!isValidStateCode(delivery)) {
      return { resolved: false, code: null, supplyType: null, source: 'UNRESOLVED', reason: 'INVALID_DELIVERY' };
    }
    return finish(delivery, 'DELIVERY', input.businessStateCode);
  }

  if (customer) {
    if (!isValidStateCode(customer)) {
      return { resolved: false, code: null, supplyType: null, source: 'UNRESOLVED', reason: 'INVALID_OVERRIDE' };
    }
    return finish(customer, 'CUSTOMER', input.businessStateCode);
  }

  // No source at all. This is the case that used to quietly become the business
  // state, and it is the whole reason `resolved` exists.
  return {
    resolved: false,
    code: null,
    supplyType: null,
    source: 'UNRESOLVED',
    reason: input.customerStateCode === undefined ? 'NO_SOURCE' : 'NO_CUSTOMER_STATE',
  };
}

function finish(
  code: string,
  source: Exclude<PlaceOfSupplySource, 'UNRESOLVED'>,
  businessStateCode: string,
): PlaceOfSupplyResolution {
  // The business state is used HERE and only here: to classify the already
  // resolved code as intra- or inter-state. It never chooses the code.
  const supplyType: SupplyType = code === businessStateCode ? 'INTRA' : 'INTER';
  return { resolved: true, code, supplyType, source };
}


/**
 * Split a tax rate across CGST/SGST (intra-state) or IGST (inter-state).
 *
 * GST slabs are quantised to 0.1% so `rateBp` is normally even and the halves
 * are exact. For an odd value the remainder paise go to SGST, which keeps
 *
 *     cgstPaise + sgstPaise  ===  round(taxable × rateBp / 10_000)
 *
 * exactly. A 1-paise discrepancy here would make the invoice fail to
 * reconcile with the GST return, so the invariant is tested, not assumed.
 */
export function splitGst(
  taxablePaise: Money,
  rateBp: number,
  supplyType: SupplyType,
): TaxSplit {
  if (rateBp === 0 || supplyType === 'EXPORT' || supplyType === 'SEZ') return ZERO_SPLIT;

  const totalPaise = roundPaise((taxablePaise * rateBp) / 10_000);

  if (supplyType === 'INTER') {
    return { ...ZERO_SPLIT, igstRateBp: rateBp, igstPaise: totalPaise };
  }

  const cgstRateBp = Math.floor(rateBp / 2);
  const sgstRateBp = rateBp - cgstRateBp;
  const cgstPaise = roundPaise((taxablePaise * cgstRateBp) / 10_000);

  return {
    cgstRateBp,
    sgstRateBp,
    igstRateBp: 0,
    cgstPaise,
    // remainder lands in SGST so the pair sums to `totalPaise` exactly
    sgstPaise: asMoney(totalPaise - cgstPaise),
    igstPaise: asMoney(0),
  };
}

/** Indian GST slabs, in basis points. 18% → 1800. */
export const GST_SLABS_BP: readonly number[] = [0, 25, 50, 100, 125, 150, 250, 300, 750, 1200, 1800, 2800];
